/**
 * Turns a failed `POST /projects` into what the create-error view shows.
 *
 * The guest project cap is recognised by its machine-readable `errorKey`, so
 * the copy is ours (translatable) and does not depend on the API's wording.
 */

/** `errorKey` the API sends when a guest's network is at the anonymous project cap. */
export const GUEST_PROJECT_LIMIT_KEY = 'guest.limit.anonProjects'

/** The message + sign-up flag the create-error view renders. */
export interface CreateErrorView {
  message: string
  requiresSignup?: boolean
}

type Translate = (
  key: string,
  values?: Record<string, string | number | boolean | Date>,
  options?: { defaultValue?: string },
) => string

/**
 * Read a rejected project-create request.
 * @param error - The caught HTTP error (`response.data` is the API body).
 * @param t - The i18n translate function.
 * @returns The view model for the create-error screen.
 */
export function createErrorView(error: unknown, t: Translate): CreateErrorView {
  const data = (
    error as {
      response?: { data?: { error?: unknown; errorKey?: unknown; requiresSignup?: unknown } }
    } | null
  )?.response?.data
  if (data?.errorKey === GUEST_PROJECT_LIMIT_KEY) {
    return {
      message: t('workspace.createError.guestProjectLimit', undefined, {
        defaultValue:
          'Guest projects are limited per network for 24 hours. Sign up to keep building.',
      }),
      requiresSignup: true,
    }
  }
  if (typeof data?.error === 'string' && data.error) {
    return { message: data.error, requiresSignup: data.requiresSignup === true }
  }
  return {
    message: t('workspace.createError.failed', undefined, {
      defaultValue: 'Failed to create project. Please try again.',
    }),
  }
}
